import { useRef } from 'react';

import { isWildfireGeoEvent } from 'features/geoEvents/geoEvent.utils';

import { ReportableGeoEvent } from './types';

export type FrozenWarningInputs = {
  initialAcreage: number | null;
  initialContainment: number | null;
  initialIsFps: boolean;
  initialManualDeactivationStarted: boolean | null;
};

/**
 * Freezes the inputs to the acreage-update warning while a report is being
 * submitted.
 *
 * During submission the geoEvent is saved and the cache is invalidated, which
 * re-renders `ReportForm` with a fresh `geoEvent` prop reflecting the just-saved
 * report. Without freezing, that mid-submit change would shift the warning's
 * baseline acreage or flip its "already under control" suppression, flashing the
 * banner on/off right as the report is submitted. Snapshotting the inputs while
 * `isSubmittingReport` is true keeps the warning stable through that window.
 */
export const useFrozenWarningInputs = (
  geoEvent: ReportableGeoEvent,
  isSubmittingReport: boolean,
): FrozenWarningInputs => {
  const getFrozenWarningInputs = (): FrozenWarningInputs =>
    isWildfireGeoEvent(geoEvent)
      ? {
          initialAcreage: geoEvent.data.acreage ?? null,
          initialContainment: geoEvent.data.containment,
          initialIsFps: geoEvent.data.isFps,
          initialManualDeactivationStarted:
            geoEvent.data.manualDeactivationStarted,
        }
      : {
          initialAcreage: null,
          initialContainment: null,
          initialIsFps: false,
          initialManualDeactivationStarted: null,
        };

  const frozenWarningInputsRef = useRef(getFrozenWarningInputs());
  if (!isSubmittingReport) {
    frozenWarningInputsRef.current = getFrozenWarningInputs();
  }

  return frozenWarningInputsRef.current;
};
